import React, {useCallback, useEffect, useState} from "react";
import Select from "react-select";
import {atom, useAtom} from "jotai";
import styled from "styled-components";
import Table from "../../components/table";
import {dataTotalInfo} from "../../components/common/entity";
import {selUserList} from "../../services/platform/ManageUserAxios";
import {
  columnUserData, mediaType, searchAccountInfo,
  selectAccountUseInfo, selectMediaSearchType
} from "./entity/common";
import PlatformSectionHeader from "./PlatformSectionHeader";
import PlatformTableBody from "./PlatformTableBody";
import {useNavigate, useSearchParams} from "react-router-dom";
import {selectUserCategories} from "../../services/platform/ManageUserCategoryAxios";

const UserInfoList = atom([]);
const getUserCategoryId = user => user?.categoryId
    ?? user?.userCategoryId
    ?? user?.category?.id
    ?? user?.userCategory?.id;
const selectStyle = {
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    height: 40,
    borderColor: state.isFocused ? "var(--color-primary)" : "#dfe3e7",
    borderRadius: 7,
    boxShadow: state.isFocused ? "0 0 0 3px rgba(59,130,246,.1)" : "none",
    fontSize: 11,
    "&:hover": {borderColor: "var(--color-primary-border)"}
  }),
  valueContainer: base => ({...base, height: 40, padding: "0 11px"}),
  indicatorsContainer: base => ({...base, height: 40}),
  indicatorSeparator: () => ({display: "none"}),
  dropdownIndicator: base => ({...base, padding: "0 9px", color: "#7c8794"}),
  menu: base => ({
    ...base,
    zIndex: 40,
    overflow: "hidden",
    border: "1px solid #dfe3e7",
    borderRadius: 7,
    boxShadow: "0 8px 24px rgba(31,41,55,.12)"
  }),
  option: (base, state) => ({
    ...base,
    padding: "9px 11px",
    backgroundColor: state.isSelected ? "var(--color-primary-soft)" : state.isFocused ? "var(--color-surface-muted)"
        : "var(--color-surface)",
    color: state.isSelected ? "var(--color-primary-strong)" : "#4b5563",
    fontSize: 11,
    cursor: "pointer"
  })
};

export default function PlatformUser() {
  const navigate = useNavigate();
  const [routeSearchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState({...searchAccountInfo});
  const [users, setUsers] = useAtom(UserInfoList);
  const [total, setTotal] = useState(dataTotalInfo);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);

  const fetchList = useCallback(async condition => {
    setLoading(true);
    try {
      const response = await selUserList(condition.categoryId ? {...condition, categoryId:null} : condition);
      const responseUsers = response?.content || response?.rows || [];
      const users = condition.categoryId
          ? responseUsers.filter(user => String(getUserCategoryId(user)) === String(condition.categoryId))
          : responseUsers;
      setTotal({
        totalCount: condition.categoryId ? users.length : response?.totalElements || response?.totalCount || users.length,
        totalPages: response?.totalPages || 0,
        currentPage: response?.pageNumber || 0
      });
      setUsers(users);
    } finally {
      setLoading(false);
    }
  }, [setUsers]);
  const routeCategoryId = routeSearchParams.get("categoryId");
  useEffect(() => {
    const categoryId = routeCategoryId ? Number(routeCategoryId) : null;
    const next = {...searchAccountInfo, categoryId};
    setSearch(next);
    fetchList(next);
  }, [fetchList, routeCategoryId]);
  useEffect(() => {
    selectUserCategories().then(setCategories).catch(() => setCategories([]));
  }, []);

  const setValue = (key, value) => setSearch(prev => ({...prev, [key]: value}));
  const changeSearchType = option => setSearch(prev => ({
    ...prev, mediaSearchType: option.value,
    siteName: "", username: "", phoneNumber: "", searchText: ""
  }));
  const changeKeyword = e => {
    const value = e.target.value;
    const field = search.mediaSearchType === "MEDIA_NAME" ? "siteName"
        : search.mediaSearchType === "MEDIA_ID" ? "username" : "phoneNumber";
    setSearch(prev => ({
      ...prev,
      siteName: "",
      username: "",
      phoneNumber: "",
      searchText: value,
      [field]: value
    }));
  };
  const reset = () => {
    const next = {...searchAccountInfo, categoryId: routeCategoryId ? Number(routeCategoryId) : null};
    setSearch(next);
    fetchList(next);
  };
  const statusColumn = {
    ...columnUserData.find(column => column.name === "status"),
    header: "사용 여부",
    width: 100,
    render: ({value}) => <StatusTag $active={value === "NORMAL"}>{value
    === "NORMAL" ? "활성화" : "비활성화"}</StatusTag>
  };
  const categoryColumn = {
    name: "categoryName",
    header: "카테고리",
    minWidth: 140,
    render: ({value, data}) => {
      const categoryName = value
          || data?.userCategoryName
          || data?.category?.name
          || data?.category?.label
          || categories.find(category => String(category.id)
              === String(data?.categoryId))?.label;
      return categoryName
          ? <CategoryTag>{categoryName}</CategoryTag>
          : <EmptyCategory>미지정</EmptyCategory>;
    }
  };
  const baseColumns = columnUserData.filter(column => column.name !== "status");
  const userColumns = [
    statusColumn,
    ...baseColumns.flatMap(column => column.name === "siteName"
        ? [column, categoryColumn]
        : [column])
  ];
  const selectedCategory = categories.find(category => String(category.id) === String(routeCategoryId));
  const openDetail = (...args) => {
    const event = args.find(arg => arg?.target || arg?.nativeEvent?.target);
    const target = event?.target || event?.nativeEvent?.target;
    if (target?.closest?.("button,a,input,select,[role='button']")) {
      return;
    }
    const row = args.find(
        arg => arg?.data && typeof arg.data === "object")?.data;
    if (row?.id) {
      navigate("/board/platformUserDetail", {state: {id: row.id}});
    }
  };

  return <Page>
    <Card $open={open}>
      <PlatformSectionHeader title="조회 조건"
                             description="매체 구분, 활성화 여부와 사용자 검색 조건으로 목록을 조회합니다."
                             collapsible open={open}
                             onToggle={() => setOpen(v => !v)}/>
      {open && <ConditionBody>
        <FilterGrid>
          <Field><Label>매체 구분</Label><Select styles={selectStyle}
                                             options={mediaType}
                                             value={mediaType.find(v => v.value
                                                     === search.mediaType)
                                                 || mediaType[0]}
                                             onChange={v => setValue(
                                                 "mediaType", v.value)}
                                             isSearchable={false}/></Field>
          <Field><Label>활성화 여부</Label><Select styles={selectStyle}
                                              options={selectAccountUseInfo}
                                              value={selectAccountUseInfo.find(
                                                      v => v.value
                                                          === search.activeYn)
                                                  || selectAccountUseInfo[0]}
                                              onChange={v => setValue(
                                                  "activeYn", v.value)}
                                              isSearchable={false}/></Field>
          <SearchField><Label>사용자 검색</Label><SearchRow>
            <TypeSelect><Select styles={selectStyle}
                                options={selectMediaSearchType}
                                value={selectMediaSearchType.find(
                                        v => v.value === search.mediaSearchType)
                                    || selectMediaSearchType[0]}
                                onChange={changeSearchType}
                                isSearchable={false}/></TypeSelect>
            <KeywordWrap><SearchIcon>⌕</SearchIcon><input
                value={search.searchText || ""} onChange={changeKeyword}
                onKeyDown={e => e.key === "Enter" && fetchList(search)}
                disabled={!search.mediaSearchType}
                placeholder={search.mediaSearchType ? "검색어를 입력해 주세요."
                    : "검색 항목을 선택해 주세요."}/></KeywordWrap>
          </SearchRow></SearchField>
        </FilterGrid>
        <Footer><Guide>조건을 설정한 뒤 적용 버튼을 눌러 사용자 목록을
          조회합니다.</Guide><Actions><ResetButton
            onClick={reset}>초기화</ResetButton><ApplyButton
            onClick={() => fetchList(search)} disabled={loading}>{loading
            ? "조회 중" : "적용"}</ApplyButton></Actions></Footer>
      </ConditionBody>}
    </Card>
    <Card>
      <PlatformSectionHeader title="사용자 목록"
                             description={selectedCategory ? `${selectedCategory.label} 카테고리에 등록된 사용자 정보와 계정 상태를 확인합니다.` : "등록된 사용자 정보와 계정 상태를 확인합니다."}
                             count={total.totalCount || 0} unit="명"/>
      <PlatformTableBody><Table columns={userColumns}
                                totalCount={[total.totalCount, "매체"]}
                                data={users || []}
                                pagination
                                limit={10}
                                defaultLimit={10}
                                onRowClick={openDetail}/></PlatformTableBody>
    </Card>
  </Page>;
}

const CategoryTag = styled.span`
  display: inline-flex;
  max-width: 100%;
  align-items: center;
  padding: 4px 9px;
  overflow: hidden;
  border: 1px solid var(--color-primary-border);
  border-radius: 999px;
  background: var(--color-primary-soft);
  color: var(--color-primary-strong);
  font-size: var(--font-size-11);
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const EmptyCategory = styled.span`
  color: var(--color-text-muted);
  font-size: var(--font-size-11);
`;
const Page = styled.div`display: flex;
  width: 100%;
  flex-direction: column;
  gap: 16px;`;
const Card = styled.section`position: relative;
  z-index: ${({$open}) => $open ? 20 : 1};
  overflow: ${({$open}) => $open ? "visible" : "hidden"};
  border: 1px solid var(--color-border);
  border-radius: 10px;
  background: var(--color-surface);
  box-shadow: 0 5px 18px rgba(31, 41, 55, .045);`;
const ConditionBody = styled.div`padding: 18px 20px 16px;
  border-top: 0;
  background: var(--color-surface);`;
const FilterGrid = styled.div`display: grid;
  grid-template-columns:minmax(180px, 1fr) minmax(180px, 1fr) minmax(360px, 2fr);
  gap: 14px;
  @media (max-width: 900px) {
    grid-template-columns:1fr 1fr;.search {
    grid-column: 1/-1;
  }
  }
  @media (max-width: 640px) {
    grid-template-columns:1fr;
  }`;
const Field = styled.div`display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 7px;`;
const SearchField = styled(Field).attrs({className: "search"})``;
const Label = styled.div`color: #444;
  font-size: var(--font-size-13);
  font-weight: 700;`;
const SearchRow = styled.div`display: flex;
  min-width: 0;`;
const TypeSelect = styled.div`width: 150px;
  flex: 0 0 150px;

  & > div > div {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }`;
const KeywordWrap = styled.div`position: relative;
  min-width: 0;
  flex: 1;

  input {
    width: 100%;
    height: 40px;
    box-sizing: border-box;
    padding: 0 34px;
    border: 1px solid #dfe3e7;
    border-left: 0;
    border-radius: 0 7px 7px 0;
    color: #444;
    font-size: var(--font-size-13);
    outline: 0;
  }

  input:focus {
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, .1);
  }

  input:disabled {
    background: #f5f6f7;
    color: #aaa;
  }`;
const SearchIcon = styled.span`position: absolute;
  left: 12px;
  top: 50%;
  z-index: 2;
  color: #9aa3ad;
  font-size: var(--font-size-19);
  transform: translateY(-52%);`;
const Footer = styled.div`display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid var(--color-divider);`;
const Guide = styled.span`color: #929ba5;
  font-size: var(--font-size-11);`;
const Actions = styled.div`display: flex;
  gap: 8px;`;
const ResetButton = styled.button`height: 38px;
  padding: 0 16px;
  border: 1px solid #dfe3e7;
  border-radius: 7px;
  background: var(--color-surface);
  color: #69737e;
  font-size: var(--font-size-13);
  font-weight: 650;
  cursor: pointer;

  &:hover {
    background: #f8fafc;
  }`;
const ApplyButton = styled(ResetButton)`border-color: var(--color-primary-strong);
  background: var(--color-primary-strong);
  color: var(--color-surface);

  &:hover {
    background: var(--color-primary-dark);
  }`;
const StatusTag = styled.span`
  display: inline-flex;
  min-width: 54px;
  height: 23px;
  align-items: center;
  justify-content: center;
  padding: 0 9px;
  border: 1px solid ${({$active}) => $active ? "#b7e4cf" : "#f2c2c2"};
  border-radius: 12px;
  background: ${({$active}) => $active ? "#ecf8f2" : "#fff1f1"};
  color: ${({$active}) => $active ? "#16845b" : "var(--color-danger-strong)"};
  font-size: var(--font-size-12);
  font-weight: 700;
`;
